import React, { useState } from 'react';
import Slider from 'react-slick';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

/* helper → always reads from /public/assets/images/ */
const img = (file) => `${process.env.PUBLIC_URL}/assets/images/${file}`;

/* ───────────────────────────── small tab block ───────────────────────────── */
const missionTabs = [
  {
    label: 'Our Mission',
    text: [
      `Our mission at Meta Mind Inc is to break down language barriers and help businesses connect deeply with Indian regional audiences through precise, culturally adapted translation services. From Hindi, Bengali, and Marathi to Tamil, Telugu, and Kannada, we ensure meaningful multilingual communication tailored to every industry and region.`,
      `We are dedicated to maintaining the highest standards in quality, confidentiality, and customer satisfaction—ensuring every project reflects our clients’ voices with cultural accuracy and linguistic excellence.`,
    ],
  },
  {
    label: 'Our Vision',
    text: [
      `Our vision is to be a leading and trusted name in Indian language translation and localization services—empowering businesses to grow across India’s rich linguistic landscape and beyond.`,
      `We continuously expand our offerings and embrace technologies like AI and machine learning to ensure fast, reliable, and culturally sensitive translations that power effective communication in every region.`,
    ],
  },
  {
    label: 'Philosophy',
    text: [
      `At Meta Mind Inc, we believe language is more than words—it’s the essence of connection and trust. Every translation we provide captures the spirit and nuance of local languages, ensuring our clients resonate with their audiences across India.`,
      `We stand by collaboration, innovation, and ethical practices, and treat every client relationship as a valued partnership built on understanding and mutual success.`,
    ],
  },
];

/* ─────────────────────────── timeline “Our Story” ────────────────────────── */
const storyTabs = [
  {
    year: '2021',
    title: 'Operational Expansion and Localization',
    img: 'tab_img1.jpg',
    p1: `In 2021, we expanded our translation operations to accommodate regional dialects across various Indian states. Our linguistic reach broadened to serve more localized audiences, ensuring accuracy and cultural relevance.`,
    bullets: [
      'Launched regional translation units across South India',
      'Increased talent acquisition in native linguistics',
      'Improved turnaround time by 15%',
    ],
  },
  {
    year: '2022',
    title: 'Technology Integration and QA Automation',
    img: 'tab_img1.jpg',
    p1: `In 2022, we successfully implemented quality assurance automation across all major translation projects, enhancing both speed and accuracy. Our commitment to continuous improvement led to a 98% client satisfaction rate.`,
    bullets: [
      'Introduced AI-powered quality checks',
      'Streamlined project tracking and reporting',
      'Enhanced data security protocols',
    ],
  },
  {
    year: '2023',
    title: 'Global Market Entry',
    img: 'tab_img1.jpg',
    p1: `2023 marked our strategic entry into the Indian regional translation market. With dedicated teams and native linguists, we established strong partnerships across Germany, France, and Italy, positioning ourselves as a multilingual translation powerhouse.`,
    bullets: [
      'Formed EU language teams',
      'Achieved ISO 17100 compliance',
      'Expanded client base by 30%',
    ],
  },
  {
    year: '2024',
    title: 'AI-Powered Solutions Launched',
    img: 'tab_img1.jpg',
    p1: `In 2024, we launched proprietary AI-powered translation and image labeling tools, significantly reducing processing time and enhancing accuracy across all projects. This innovation became a cornerstone of our service delivery.`,
    bullets: [
      'AI-assisted proofreading module introduced',
      'Expanded services into data annotation and labeling',
      'Recognized as a top innovator in AI translation',
    ],
  },
  {
    year: '2025',
    title: 'Establishing Global Leadership',
    img: 'tab_img1.jpg',
    p1: `By 2025, our organization has solidified its position as a global leader in professional translation and AI-enhanced linguistic services. We remain committed to client satisfaction, ethical operations, and continuous innovation.`,
    bullets: [
      'Launched 24/7 multilingual customer support',
      'Expanded to North American markets',
      'Launched R&D center for linguistic AI',
    ],
  },
];

/* ───────────────────────────── testimonials ──────────────────────────────── */
const testimonials = [
  {
    name: 'Rohan Mehta',
    quote:
      'We’ve worked with Meta Mind Inc on multilingual website content, and their translations are always precise and culturally adapted. Excellent team!',
  },
  {
    name: 'Neha Sharma',
    quote:
      'Meta Mind Inc delivered accurate and well-formatted translation of our technical manuals. Their professionalism and turnaround time are impressive.',
  },
  {
    name: 'Priya Deshpande',
    quote:
      'Thanks to Meta Mind Inc, we successfully localized our marketing campaigns in over 10 languages. Their team is both knowledgeable and efficient.',
  },
  {
    name: 'Arjun Patel',
    quote:
      'We had urgent needs for certified translations. Meta Mind Inc provided fast delivery and exceptional accuracy. Highly recommended for legal translations.',
  },
];

const slick = {
  slidesToShow: 3,
  slidesToScroll: 1,
  arrows: false,
  dots: true,
  infinite: true,
  autoplay: false,
  responsive: [
    { breakpoint: 1199, settings: { slidesToShow: 3 } },
    { breakpoint: 992,  settings: { slidesToShow: 2 } },
    { breakpoint: 620,  settings: { slidesToShow: 1 } },
  ],
};

export default function About() {
  const [missionIdx, setMissionIdx] = useState(0);
  const [storyIdx, setStoryIdx] = useState(0);

  return (
    <div className="page">
      {/* ───── PAGE-TITLE STRIPE ───── */}
      <div className="ttm-page-title-row">
        <div className="ttm-page-title-row-inner">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-12">
                <div className="page-title-heading ttm-textcolor-white">
                  <h2 className="title">About Us</h2>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ───── SITE-MAIN ───── */}
      <div className="site-main">
        {/* ========== 1 ▸ ABOUT SECTION ========== */}
        <section className="ttm-row about-section clearfix">
          <div className="container">
            <div className="row">
              {/* left column */}
              <div className="col-lg-6 col-md-12">
                <div className="pr-60 res-991-pr-0 res-991-pb-50">
                  <div className="section-title">
                    <div className="title-header">
                      <h5>ABOUT US</h5>
                      <h2 className="title">
                        We Have The Best Experts To Elevate Your Business.
                      </h2>
                    </div>
                  </div>

                  {/* small tabs */}
                  <div className="ttm-tabs ttm-tab-style-01 pt-15" data-effect="fadeIn">
                    <ul className="tabs clearfix">
                      {missionTabs.map((t, i) => (
                        <li key={t.label} className={`tab ${i === missionIdx ? 'active' : ''}`}>
                          <a href="#!" onClick={() => setMissionIdx(i)}>{t.label}</a>
                        </li>
                      ))}
                    </ul>

                    <div className="content-tab">
                      {missionTabs.map((t, i) => (
                        <div key={t.label} className={`content-inner ${i === missionIdx ? 'active' : ''}`}>
                          {t.text.map((p, k) => <p key={k}>{p}</p>)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* right column – stacked images */}
              <div className="col-lg-6 col-md-7 col-sm-8 col-10 ml-auto m-sm-auto">
                <div className="row align-items-center ttm-boxes-spacing-20px mb_20">
                  <div className="col-lg-6 col-sm-6 col-5 ttm-box-col-wrapper">
                    <div className="ttm_single_image-wrapper ml_50">
                      <img className="img-fluid" src={img('single-img-09.jpg')} alt="about" />
                    </div>
                  </div>
                  <div className="col-lg-6 col-sm-6 col-7 ttm-box-col-wrapper">
                    <div className="ttm_single_image-wrapper">
                      <img className="img-fluid" src={img('single-img-10.jpg')} alt="about" />
                    </div>
                    <div className="ttm-bgcolor-skincolor pt-20 pl-25 pr-15 pb-15 mt-15">
                      <p className="mb-0">Call Us For Free</p>
                      <h3 className="mb-0">9876552511</h3>
                    </div>
                  </div>
                </div>
              </div>
            </div>{/* row */}
          </div>
        </section>

        {/* ========== 2 ▸ OUR STORY (timeline tabs) ========== */}
        <section className="ttm-row tab-section ttm-bgcolor-grey ttm-bgimage-yes bg-img3 ttm-bg clearfix">
          <div className="ttm-row-wrapper-bg-layer ttm-bg-layer" />
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="section-title title-style-center_text">
                  <div className="title-header">
                    <h5>OUR STORY</h5>
                    <h2 className="title">Here Are Some Major Milestones</h2>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-lg-12">
                <div className="ttm-tabs ttm-tab-style-02">
                  <ul className="tabs clearfix">
                    {storyTabs.map((t, i) => (
                      <li key={t.year} className={`tab ${i === storyIdx ? 'active' : ''}`}>
                        <a href="#!" onClick={() => setStoryIdx(i)}>{t.year}</a>
                      </li>
                    ))}
                  </ul>

                  <div className="content-tab">
                    {storyTabs.map((t, i) => (
                      <div key={t.year} className={`content-inner ${i === storyIdx ? 'active' : ''}`}>
                        <div className="row align-items-center">
                          {i % 2 === 0 ? (
                            <>
                              <div className="col-lg-6 col-md-9 col-sm-11">
                                <div className="ttm_single_image-wrapper">
                                  <img className="img-fluid m-0" src={img(t.img)} alt={t.year} />
                                </div>
                              </div>
                              <div className="col-lg-6 col-md-12">
                                <div className="pl-15 res-991-pl-0 res-991-pt-30">
                                  <h3>{t.title}</h3>
                                  <p>{t.p1}</p>
                                  <ul className="ttm-list ttm-list-style-icon ttm-list-icon-color-skincolor">
                                    {t.bullets.map((b) => (
                                      <li key={b}>
                                        <i className="fa fa-arrow-circle-right" />
                                        <div className="ttm-list-li-content">{b}</div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="col-lg-6 col-md-12">
                                <div className="pr-15 res-991-pr-0 res-991-pb-30">
                                  <h3>{t.title}</h3>
                                  <p>{t.p1}</p>
                                  <ul className="ttm-list ttm-list-style-icon ttm-list-icon-color-skincolor">
                                    {t.bullets.map((b) => (
                                      <li key={b}>
                                        <i className="fa fa-arrow-circle-right" />
                                        <div className="ttm-list-li-content">{b}</div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                              <div className="col-lg-6 col-md-9 col-sm-11">
                                <div className="ttm_single_image-wrapper">
                                  <img className="img-fluid m-0" src={img(t.img)} alt={t.year} />
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>{/* content-tab */}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========== 3 ▸ TESTIMONIALS ========== */}
        <section className="ttm-row testimonial-section ttm-bgcolor-darkgrey ttm-bgimage-yes bg-img1 ttm-bg clearfix">
          <div className="ttm-row-wrapper-bg-layer ttm-bg-layer" />
          <div className="container">
            <div className="row">
              <div className="col-lg-12">
                <div className="section-title title-style-center_text">
                  <div className="title-header">
                    <h5>TESTIMONIALS</h5>
                    <h2 className="title">What Our Clients Say?</h2>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <Slider {...slick} className="slick_slider slick-dots-style2 text-center">
                {testimonials.map((t) => (
                  <div key={t.name} className="col-lg-4">
                    <div className="testimonials style1">
                      <div className="testimonial-avatar" />
                      <div className="testimonial-content">
                        <i className="ti-quote-left" />
                        <blockquote>{t.quote}</blockquote>
                        <div className="testimonial-caption">
                          <h5>{t.name}</h5>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        </section>

        {/* ========== 4 ▸ HOW IT WORKS ========== */}
        <section className="ttm-row features-section clearfix">
          <div className="container">
            <div className="row">
              <div className="col-lg-6 col-md-8 col-sm-9 m-auto">
                <div className="section-title title-style-center_text">
                  <div className="title-header">
                    <h5>How It Works</h5>
                    <h2 className="title">Explore Our Translation Workflow</h2>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mb_15">
              {[
                {
                  icon: 'flaticon-customer-service',
                  title: 'Submit Your Request',
                  desc: 'Select the language pair and submit your content for translation via our secure portal.',
                },
                {
                  icon: 'flaticon-call-center-1',
                  title: 'Project Assessment',
                  desc: 'Our team evaluates the document type, domain, and scope to assign the right linguistic experts.',
                },
                {
                  icon: 'flaticon-customer-service-1',
                  title: 'Translation & Review',
                  desc: 'Certified translators deliver high-quality translations followed by rigorous proofreading and QA checks.',
                },
                {
                  icon: 'flaticon-headset',
                  title: 'Delivery & Support',
                  desc: 'Final deliverables are submitted on time, with post-delivery support for revisions and client feedback.',
                },
              ].map((f) => (
                <div key={f.title} className="col-lg-3 col-md-4 col-sm-6 m-auto">
                  <div className="featured-icon-box icon-align-top-content style5">
                    <div className="featured-icon">
                      <div className="ttm-icon ttm-icon_element-color-skincolor ttm-icon_element-onlytxt ttm-icon_element-size-lg">
                        <i className={`flaticon ${f.icon}`} />
                      </div>
                    </div>
                    <div className="featured-content">
                      <div className="featured-title">
                        <h5>{f.title}</h5>
                      </div>
                      <div className="featured-desc">
                        <p>{f.desc}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>{/* site-main */}
    </div>
  );
}